import type { GridsterEngine } from './gridster-engine';
import type { GridsterItemInstance } from './gridster-item-instance';

export const PUSH_DIRECTION = {
  fromNorth: 'fromNorth',
  fromEast: 'fromEast',
  fromSouth: 'fromSouth',
  fromWest: 'fromWest'
} as const;

export type PushDirection = (typeof PUSH_DIRECTION)[keyof typeof PUSH_DIRECTION];

export class GridsterPush {
  private pushedItems: GridsterItemInstance[] = [];
  private pushedItemsTemp: GridsterItemInstance[] = [];
  private pushedItemsTempPath: { x: number; y: number }[][] = [];
  private pushedItemsPath: { x: number; y: number }[][] = [];
  private pushedItemsOrder: GridsterItemInstance[] = [];
  private iteration = 0;

  constructor(
    private gridsterItem: GridsterItemInstance,
    private gridster: GridsterEngine
  ) {}

  pushItems(direction: string, disable?: boolean): boolean {
    if (!this.gridster.options.pushItems || disable) {
      return false;
    }
    this.pushedItemsOrder = [];
    this.iteration = 0;
    const pushed = this.push(this.gridsterItem, direction);
    if (!pushed) {
      this.restoreTempItems();
    }
    this.pushedItemsOrder = [];
    this.pushedItemsTemp = [];
    this.pushedItemsTempPath = [];
    return pushed;
  }

  restoreTempItems(): void {
    for (let i = this.pushedItemsTemp.length - 1; i > -1; i--) {
      this.removeFromTempPushed(this.pushedItemsTemp[i]);
    }
  }

  restoreItems(): void {
    for (const pushedItem of this.pushedItems) {
      pushedItem.$item.x = pushedItem.item.x || 0;
      pushedItem.$item.y = pushedItem.item.y || 0;
      pushedItem.setSize();
    }
    this.pushedItems = [];
    this.pushedItemsPath = [];
  }

  setPushedItems(): void {
    for (const pushedItem of this.pushedItems) {
      pushedItem.checkItemChanges(pushedItem.$item, pushedItem.item);
    }
    this.pushedItems = [];
    this.pushedItemsPath = [];
  }

  checkPushBack(): void {
    let change = false;
    for (let i = this.pushedItems.length - 1; i > -1; i--) {
      if (this.checkPushedItem(this.pushedItems[i], i)) {
        change = true;
      }
    }
    if (change) {
      this.checkPushBack();
    }
  }

  private push(gridsterItem: GridsterItemInstance, direction: string): boolean {
    if (this.iteration > 100) {
      return false;
    }
    if (this.gridster.checkGridCollision(gridsterItem.$item)) {
      return false;
    }
    if (direction === '') {
      return false;
    }

    const conflicts = this.gridster.findItemsWithItem(gridsterItem.$item);
    const invert = direction === PUSH_DIRECTION.fromNorth || direction === PUSH_DIRECTION.fromWest;

    conflicts.sort((a, b) =>
      invert
        ? b.$item.y - a.$item.y || b.$item.x - a.$item.x
        : a.$item.y - b.$item.y || a.$item.x - b.$item.x
    );

    let makePush = true;
    const pushedItems: GridsterItemInstance[] = [];

    for (const itemCollision of conflicts) {
      if (itemCollision === this.gridsterItem) {
        continue;
      }
      if (!itemCollision.canBeDragged()) {
        makePush = false;
        break;
      }
      const tempIndex = this.pushedItemsTemp.indexOf(itemCollision);
      if (tempIndex > -1 && this.pushedItemsTempPath[tempIndex].length > 10) {
        makePush = false;
        break;
      }
      if (this.tryPattern(direction, itemCollision, gridsterItem)) {
        this.pushedItemsOrder.push(itemCollision);
        pushedItems.push(itemCollision);
      } else {
        makePush = false;
        break;
      }
    }

    if (!makePush && pushedItems.length) {
      const index = this.pushedItemsOrder.lastIndexOf(pushedItems[0]);
      if (index > -1) {
        for (let j = this.pushedItemsOrder.length - 1; j >= index; j--) {
          const itemCollision = this.pushedItemsOrder[j];
          this.pushedItemsOrder.pop();
          this.removeFromTempPushed(itemCollision);
          this.removeFromPushedItem(itemCollision);
        }
      }
    }

    this.iteration++;
    return makePush;
  }

  private tryPattern(
    direction: string,
    itemCollide: GridsterItemInstance,
    item: GridsterItemInstance
  ): boolean {
    switch (direction) {
      case PUSH_DIRECTION.fromEast:
        return (
          this.tryWest(itemCollide, item) ||
          this.trySouth(itemCollide, item) ||
          this.tryNorth(itemCollide, item)
        );
      case PUSH_DIRECTION.fromWest:
        return (
          this.tryEast(itemCollide, item) ||
          this.trySouth(itemCollide, item) ||
          this.tryNorth(itemCollide, item)
        );
      case PUSH_DIRECTION.fromNorth:
        return (
          this.trySouth(itemCollide, item) ||
          this.tryEast(itemCollide, item) ||
          this.tryWest(itemCollide, item)
        );
      case PUSH_DIRECTION.fromSouth:
        return (
          this.tryNorth(itemCollide, item) ||
          this.tryEast(itemCollide, item) ||
          this.tryWest(itemCollide, item)
        );
      default:
        return false;
    }
  }

  private trySouth(itemCollide: GridsterItemInstance, item: GridsterItemInstance): boolean {
    if (!this.gridster.options.pushDirections.south) {
      return false;
    }
    this.addToTempPushed(itemCollide);
    itemCollide.$item.y = item.$item.y + item.$item.rows;
    if (this.push(itemCollide, PUSH_DIRECTION.fromNorth)) {
      itemCollide.setSize();
      this.addToPushed(itemCollide);
      return true;
    }
    this.removeFromTempPushed(itemCollide);
    return false;
  }

  private tryNorth(itemCollide: GridsterItemInstance, item: GridsterItemInstance): boolean {
    if (!this.gridster.options.pushDirections.north) {
      return false;
    }
    this.addToTempPushed(itemCollide);
    itemCollide.$item.y = item.$item.y - itemCollide.$item.rows;
    if (this.push(itemCollide, PUSH_DIRECTION.fromSouth)) {
      itemCollide.setSize();
      this.addToPushed(itemCollide);
      return true;
    }
    this.removeFromTempPushed(itemCollide);
    return false;
  }

  private tryEast(itemCollide: GridsterItemInstance, item: GridsterItemInstance): boolean {
    if (!this.gridster.options.pushDirections.east) {
      return false;
    }
    this.addToTempPushed(itemCollide);
    itemCollide.$item.x = item.$item.x + item.$item.cols;
    if (this.push(itemCollide, PUSH_DIRECTION.fromWest)) {
      itemCollide.setSize();
      this.addToPushed(itemCollide);
      return true;
    }
    this.removeFromTempPushed(itemCollide);
    return false;
  }

  private tryWest(itemCollide: GridsterItemInstance, item: GridsterItemInstance): boolean {
    if (!this.gridster.options.pushDirections.west) {
      return false;
    }
    this.addToTempPushed(itemCollide);
    itemCollide.$item.x = item.$item.x - itemCollide.$item.cols;
    if (this.push(itemCollide, PUSH_DIRECTION.fromEast)) {
      itemCollide.setSize();
      this.addToPushed(itemCollide);
      return true;
    }
    this.removeFromTempPushed(itemCollide);
    return false;
  }

  private addToTempPushed(gridsterItem: GridsterItemInstance): void {
    let index = this.pushedItemsTemp.indexOf(gridsterItem);
    if (index === -1) {
      index = this.pushedItemsTemp.push(gridsterItem) - 1;
      this.pushedItemsTempPath[index] = [];
    }
    this.pushedItemsTempPath[index].push({
      x: gridsterItem.$item.x,
      y: gridsterItem.$item.y
    });
  }

  private removeFromTempPushed(gridsterItem: GridsterItemInstance): void {
    const index = this.pushedItemsTemp.indexOf(gridsterItem);
    if (index === -1) {
      return;
    }
    const tempPosition = this.pushedItemsTempPath[index].pop();
    if (!tempPosition) {
      return;
    }
    gridsterItem.$item.x = tempPosition.x;
    gridsterItem.$item.y = tempPosition.y;
    gridsterItem.setSize();
    if (!this.pushedItemsTempPath[index].length) {
      this.pushedItemsTemp.splice(index, 1);
      this.pushedItemsTempPath.splice(index, 1);
    }
  }

  private addToPushed(gridsterItem: GridsterItemInstance): void {
    const index = this.pushedItems.indexOf(gridsterItem);
    if (index < 0) {
      this.pushedItems.push(gridsterItem);
      this.pushedItemsPath.push([
        { x: gridsterItem.item.x || 0, y: gridsterItem.item.y || 0 },
        { x: gridsterItem.$item.x, y: gridsterItem.$item.y }
      ]);
    } else {
      this.pushedItemsPath[index].push({
        x: gridsterItem.$item.x,
        y: gridsterItem.$item.y
      });
    }
  }

  private removeFromPushedItem(gridsterItem: GridsterItemInstance): void {
    const index = this.pushedItems.indexOf(gridsterItem);
    if (index > -1) {
      this.pushedItemsPath[index].pop();
      if (!this.pushedItemsPath[index].length) {
        this.pushedItems.splice(index, 1);
        this.pushedItemsPath.splice(index, 1);
      }
    }
  }

  private checkPushedItem(pushedItem: GridsterItemInstance, index: number): boolean {
    const path = this.pushedItemsPath[index];
    let change = false;
    for (let j = path.length - 2; j > -1; j--) {
      const lastPosition = path[j];
      const x = pushedItem.$item.x;
      const y = pushedItem.$item.y;
      pushedItem.$item.x = lastPosition.x;
      pushedItem.$item.y = lastPosition.y;
      if (!this.gridster.findItemWithItem(pushedItem.$item)) {
        pushedItem.setSize();
        path.splice(j + 1, path.length - j - 1);
        change = true;
      } else {
        pushedItem.$item.x = x;
        pushedItem.$item.y = y;
      }
    }
    if (path.length < 2) {
      this.pushedItems.splice(index, 1);
      this.pushedItemsPath.splice(index, 1);
    }
    return change;
  }
}
